(() => {
  class InternalVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.playButton = this.querySelector('.internal-video__play');
      this.soundButton = this.querySelector('.internal-video__sound-btn');
      this.fullscreenButton = this.querySelector('.internal-video__fullscreen-btn');
      this.timeline = this.querySelector('.internal-video__timeline');

      this.promoteSources();

      this.playButton?.addEventListener('click', this.togglePlay.bind(this));
      this.soundButton?.addEventListener('click', this.toggleSound.bind(this));
      this.fullscreenButton?.addEventListener('click', this.toggleFullscreen.bind(this));
      this.timeline && this.video.addEventListener('timeupdate', this.updateTimeline.bind(this));

      this.video.addEventListener('play', () => this.classList.add('internal-video--playing'));
      this.video.addEventListener('pause', () => this.classList.remove('internal-video--playing'));

      if (this.dataset.autoplay === 'true') this.setupAutoplay();
    }

    promoteSources() {
      const sources = this.video.querySelectorAll('source[data-src]');
      if (sources.length === 0) return;
      sources.forEach((source) => {
        source.setAttribute('src', source.getAttribute('data-src'));
        source.removeAttribute('data-src');
      });
      this.video.load();
    }

    setupAutoplay() {
      this.autoplayObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.video.play().catch(() => {});
            } else {
              this.video.pause();
            }
          });
        },
        { threshold: 0.5 }
      );
      this.autoplayObserver.observe(this);
    }

    togglePlay() {
      if (this.video.paused) {
        this.video.play().catch(() => {});
      } else {
        this.video.pause();
      }
    }

    toggleSound() {
      this.video.muted = !this.video.muted;
      this.classList.toggle('internal-video--muted', this.video.muted);
    }

    toggleFullscreen() {
      if (this.video.requestFullscreen) this.video.requestFullscreen();
      else if (this.video.webkitEnterFullscreen) this.video.webkitEnterFullscreen();
    }

    updateTimeline() {
      if (!this.video.duration) return;
      const percent = (this.video.currentTime / this.video.duration) * 100;
      this.timeline.style.setProperty('--progress', `${percent}%`);
    }

    disconnectedCallback() {
      this.autoplayObserver?.disconnect();
    }
  }

  if (!customElements.get('internal-video')) customElements.define('internal-video', InternalVideo);

  class BeforeAfterComparison extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slider]');
      this.divider = this.querySelector('[data-divider]');
      this.rightImage = this.querySelector('[data-right-image]');
      this.annotations = this.querySelectorAll('[data-annotation]');

      if (!this.slider || !this.divider || !this.rightImage) return;

      this.isDragging = false;
      this.boundOnDrag = this.onDrag.bind(this);
      this.boundStopDrag = this.stopDrag.bind(this);
      this.boundPositionAnnotations = this.positionAnnotations.bind(this);

      this.setupEventListeners();
      this.positionAnnotations();
      this.updateAnnotationVisibility(50);

      window.addEventListener('resize', this.boundPositionAnnotations);
    }

    disconnectedCallback() {
      document.removeEventListener('mousemove', this.boundOnDrag);
      document.removeEventListener('touchmove', this.boundOnDrag);
      document.removeEventListener('mouseup', this.boundStopDrag);
      document.removeEventListener('touchend', this.boundStopDrag);
      window.removeEventListener('resize', this.boundPositionAnnotations);
    }

    setupEventListeners() {
      this.divider.addEventListener('mousedown', (e) => this.startDrag(e));
      this.divider.addEventListener('touchstart', (e) => this.startDrag(e), { passive: true });

      document.addEventListener('mousemove', this.boundOnDrag);
      document.addEventListener('touchmove', this.boundOnDrag, { passive: false });

      document.addEventListener('mouseup', this.boundStopDrag);
      document.addEventListener('touchend', this.boundStopDrag);
    }

    startDrag(e) {
      this.isDragging = true;
      this.sliderRect = this.slider.getBoundingClientRect();
      e.preventDefault();
    }

    onDrag(e) {
      if (!this.isDragging) return;

      this.sliderRect = this.slider.getBoundingClientRect();
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      let offsetX = clientX - this.sliderRect.left;
      offsetX = Math.max(0, Math.min(offsetX, this.sliderRect.width));

      const percentage = (offsetX / this.sliderRect.width) * 100;
      this.updateSlider(percentage);
    }

    stopDrag() {
      this.isDragging = false;
    }

    updateSlider(percentage) {
      this.divider.style.left = percentage + '%';
      this.rightImage.style.clipPath = `inset(0 0 0 ${percentage}%)`;
      this.updateAnnotationVisibility(percentage);
    }

    positionAnnotations() {
      this.annotations.forEach((annotation) => {
        const direction = annotation.dataset.direction;
        const text = annotation.querySelector('[data-text]');
        const line = annotation.querySelector('[data-line]');

        if (!text || !line) return;

        const lineLength = 60;
        const isRight = direction === 'right';

        line.style.width = lineLength + 'px';
        line.style.left = isRight ? '5px' : '-' + (lineLength + 5) + 'px';
        line.style.top = '-1px';

        text.style.left = isRight ? (lineLength + 15) + 'px' : '-' + (lineLength + 15) + 'px';
        text.style.top = '-50%';
        text.style.transform = isRight ? 'translateY(-50%)' : 'translate(-100%, -50%)';
      });
    }

    updateAnnotationVisibility(sliderPercentage) {
      this.annotations.forEach((annotation) => {
        const side = annotation.dataset.side;
        const annotationX = parseFloat(annotation.dataset.x);

        let isVisible = false;
        if (side === 'left' && annotationX < sliderPercentage) {
          isVisible = true;
        } else if (side === 'right' && annotationX >= sliderPercentage) {
          isVisible = true;
        }

        annotation.style.opacity = isVisible ? '1' : '0';
      });
    }
  }

  if (!customElements.get('before-after-comparison')) {
    customElements.define('before-after-comparison', BeforeAfterComparison);
  }

  const initializeComparisonSlider = (slider) => {
    if (slider.dataset.initialized === 'true') return;

    const input = slider.querySelector('.comparison-slider__input');
    const overlay = slider.querySelector('.comparison-slider__overlay');
    const line = slider.querySelector('.comparison-slider__line');
    if (!input || !overlay || !line) return;

    slider.dataset.initialized = 'true';

    const updatePosition = () => {
      const value = `${input.value}%`;
      overlay.style.width = value;
      line.style.left = value;
      slider.style.setProperty('--position', value);
    };

    input.addEventListener('input', updatePosition);
    input.addEventListener('change', updatePosition);
    updatePosition();
  };

  class PromoPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.overlay = this.querySelector('.sign-up-popup-overlay');
      this.modal = this.querySelector('.sign-up-popup-modal');
      this.boundKeydown = this.handleKeydown.bind(this);
      this.querySelectorAll('.promp-popup__close-btn').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.overlay?.addEventListener('click', () => this.close());

      if (this.dataset.testMode !== 'true') {
        const storageKey = `promo-popup-dismissed-${window.location.host}`;
        let dismissedAt = 0;
        try {
          dismissedAt = Number(localStorage.getItem(storageKey) || 0);
        } catch (error) {
          // Continue without frequency tracking when storage is unavailable.
        }
        const frequency = Number(this.dataset.delayDays || 0) * 86400000;
        if (!dismissedAt || Date.now() - dismissedAt >= frequency) {
          this.openTimeout = window.setTimeout(() => this.open(), Number(this.dataset.delaySeconds || 0) * 1000);
        }
      }

      if (this.dataset.displayTimer === 'true') this.startTimer();
      this.bindSuccessPopup();
      if (this.modal?.classList.contains('popup-modal--active')) this.focusModal();
    }

    open() {
      this.previousFocus = document.activeElement;
      this.modal?.classList.add('popup-modal--active');
      this.overlay?.classList.add('popup-overlay--active');
      document.addEventListener('keydown', this.boundKeydown);
      this.focusModal();
    }

    close() {
      this.modal?.classList.remove('popup-modal--active');
      this.overlay?.classList.remove('popup-overlay--active');
      document.removeEventListener('keydown', this.boundKeydown);
      this.previousFocus?.focus?.();
      if (this.dataset.testMode !== 'true') {
        try {
          localStorage.setItem(`promo-popup-dismissed-${window.location.host}`, String(Date.now()));
        } catch (error) {
          // The popup can still be dismissed when storage is unavailable.
        }
      }
    }

    focusModal() {
      const focusable = this.modal?.querySelector(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      (focusable || this.modal)?.focus?.();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.modal?.classList.contains('popup-modal--active')) return;
      const focusable = [...this.modal.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    startTimer() {
      const minutes = this.querySelector('.popup-modal__timer__minutes');
      const seconds = this.querySelector('.popup-modal__timer__seconds');
      if (!minutes || !seconds) return;

      let remaining = Math.round(Number(this.dataset.timerDuration || 0) * 60);
      const update = () => {
        minutes.textContent = String(Math.floor(remaining / 60)).padStart(2, '0');
        seconds.textContent = String(remaining % 60).padStart(2, '0');
        if (remaining > 0) remaining -= 1;
      };
      update();
      this.timerInterval = window.setInterval(update, 1000);
    }

    markSubscribed() {
      window.clearTimeout(this.openTimeout);
      if (this.dataset.testMode !== 'true') {
        try {
          localStorage.setItem(`promo-popup-dismissed-${window.location.host}`, String(Date.now()));
        } catch (error) {
          // Continue without persisting dismissal when storage is unavailable.
        }
      }
    }

    bindSuccessPopup() {
      const overlay = document.querySelector('.success-popup-overlay');
      const modal = document.querySelector('.success-popup-modal');
      if (!overlay || !modal) return;
      if (overlay.dataset.resdorBound === 'true') return;
      overlay.dataset.resdorBound = 'true';
      const close = () => {
        overlay.classList.remove('popup-overlay--active');
        modal.classList.remove('popup-modal--active');
      };
      overlay.addEventListener('click', close);
      modal.querySelector('.popup-modal__close')?.addEventListener('click', close);
      modal.querySelector('.popup-modal__dismiss-btn')?.addEventListener('click', close);
    }

    disconnectedCallback() {
      window.clearTimeout(this.openTimeout);
      window.clearInterval(this.timerInterval);
      document.removeEventListener('keydown', this.boundKeydown);
    }
  }

  if (!customElements.get('promo-popup')) customElements.define('promo-popup', PromoPopup);

  const ENGLISH_WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const splitLabels = (value) => {
    if (!value) return [];
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  };

  const pad2 = (number) => String(number).padStart(2, '0');

  const ordinal = (day) => {
    const remainder100 = day % 100;
    if (remainder100 >= 11 && remainder100 <= 13) return `${day}th`;
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  };

  const addShippingDays = (daysToAdd, skipWeekdays, skipDates) => {
    const date = new Date();
    let added = 0;
    let guard = 0;
    while (added < daysToAdd && guard < 3650) {
      date.setDate(date.getDate() + 1);
      guard += 1;
      const weekday = ENGLISH_WEEKDAYS[date.getDay()];
      const monthDay = `${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
      if (skipWeekdays.includes(weekday) || skipDates.includes(monthDay)) continue;
      added += 1;
    }
    return date;
  };

  const formatShippingDate = (date, format, dayLabels, monthLabels) => {
    const weekdayIndex = (date.getDay() + 6) % 7; // dayLabels is Monday-first
    const dayLabel = dayLabels[weekdayIndex] || '';
    const monthLabel = monthLabels[date.getMonth()] || '';
    const day = date.getDate();
    const paddedDay = pad2(day);
    const paddedMonth = pad2(date.getMonth() + 1);

    switch (format) {
      case 'day_dd_mm':
        return `${dayLabel}, ${day}. ${monthLabel}`;
      case 'mm_dd':
        return `${monthLabel} ${ordinal(day)}`;
      case 'dd_mm':
        return `${day}. ${monthLabel}`;
      case 'dd_mm_no_dot':
        return `${day} ${monthLabel}`;
      case 'day_dd_mm_numeric':
        return `${dayLabel}, ${paddedDay}. ${paddedMonth}.`;
      case 'dd_mm_numeric':
        return `${paddedDay}. ${paddedMonth}.`;
      case 'day_mm_dd':
      default:
        return `${dayLabel}, ${monthLabel} ${ordinal(day)}`;
    }
  };

  class DynamicDates extends HTMLElement {
    connectedCallback() {
      const dayLabels = splitLabels(this.dataset.dayLabels);
      const monthLabels = splitLabels(this.dataset.monthLabels);
      const skipWeekdays = splitLabels(this.dataset.daysToSkip).map((item) => item.toLowerCase());
      const skipDates = splitLabels(this.dataset.datesToSkip);
      const format = this.dataset.dateFormat || 'day_mm_dd';

      this.querySelectorAll('[data-dynamic-date="true"]').forEach((element) => {
        const minDays = parseInt(element.dataset.minDays, 10);
        const maxDays = parseInt(element.dataset.maxDays, 10);
        if (!Number.isFinite(minDays) || !Number.isFinite(maxDays)) return;

        const startDate = addShippingDays(minDays, skipWeekdays, skipDates);
        const endDate = addShippingDays(maxDays, skipWeekdays, skipDates);
        const text = element.dataset.text || '';

        element.innerHTML = text
          .replace(/\[start_date\]/g, formatShippingDate(startDate, format, dayLabels, monthLabels))
          .replace(/\[end_date\]/g, formatShippingDate(endDate, format, dayLabels, monthLabels));
      });
    }
  }

  if (!customElements.get('dynamic-dates')) customElements.define('dynamic-dates', DynamicDates);

  const resolveSection = (rawId) => {
    if (!rawId) return null;
    const trimmed = rawId.trim();
    if (!trimmed) return null;
    try {
      const bySelector = document.querySelector(trimmed);
      if (bySelector) return bySelector;
    } catch (error) {
      // Not a valid selector; fall through to ID-based lookups.
    }
    const withoutHash = trimmed.replace(/^#/, '');
    return document.getElementById(withoutHash) || document.getElementById(`shopify-section-${withoutHash}`);
  };

  class SectionGroup extends HTMLElement {
    connectedCallback() {
      const containerOne = this.querySelector('.section-group__section-one-container');
      const containerTwo = this.querySelector('.section-group__section-two-container');

      const moveSection = (rawId, container) => {
        if (!container) return;
        const target = resolveSection(rawId);
        if (target && target !== container && !container.contains(target)) {
          container.appendChild(target);
        }
      };

      moveSection(this.dataset.sectionOneId, containerOne);
      moveSection(this.dataset.sectionTwoId, containerTwo);
    }
  }

  if (!customElements.get('section-group')) customElements.define('section-group', SectionGroup);

  class BlogContents extends HTMLElement {
    connectedCallback() {
      this.innerHTML = '';

      const sectionId = this.dataset.section;
      const contentEl = document.getElementById(`BlogContent-${sectionId}`);
      if (!contentEl) return;

      const headings = Array.from(contentEl.querySelectorAll('h2'));
      if (!headings.length) {
        this.style.display = 'none';
        return;
      }

      const ol = document.createElement('ol');
      ol.className = 'blog-contents__list';

      headings.forEach((h2, idx) => {
        const id = `${sectionId}-${idx + 1}`;
        h2.id = id;

        const li = document.createElement('li');
        li.className = 'blog-contents__item';

        const a = document.createElement('a');
        a.href = `#${id}`;
        a.textContent = h2.textContent;
        a.className = 'blog-contents__link';

        li.appendChild(a);
        ol.appendChild(li);
      });

      if (this.dataset.heading) {
        const heading = document.createElement('h2');
        heading.textContent = this.dataset.heading;
        this.appendChild(heading);
      }
      this.appendChild(ol);
      this.classList.add('blog-contents--complete');
    }
  }

  if (!customElements.get('blog-contents')) customElements.define('blog-contents', BlogContents);

  const hardenContactForm = () => {
    const ContactForm = customElements.get('contact-form');
    if (!ContactForm || ContactForm.prototype.__resdorTncGuarded) return;

    const originalSubmit = ContactForm.prototype.handleFormSubmit;
    ContactForm.prototype.handleFormSubmit = function (event) {
      if (this.tnc && this.tnc.dataset.checked !== 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const warning = document.querySelector(
          `.tnc-checkbox-warning--${this.tnc.dataset.warningPosition}-${this.tnc.dataset.section}`
        );
        warning?.classList.remove('hidden');
        return false;
      }
      return originalSubmit.call(this, event);
    };
    ContactForm.prototype.__resdorTncGuarded = true;
  };

  const hardenSplide = () => {
    const SplideComponent = customElements.get('splide-component');
    if (!SplideComponent || SplideComponent.prototype.__resdorInitGuarded) return;

    const originalInit = SplideComponent.prototype.initSlider;
    SplideComponent.prototype.initSlider = function () {
      if (this.dataset.resdorSliderInitialized === 'true') return;
      this.dataset.resdorSliderInitialized = 'true';
      return originalInit.call(this);
    };
    document.querySelectorAll('splide-component').forEach((element) => {
      element.dataset.resdorSliderInitialized = 'true';
    });
    SplideComponent.prototype.__resdorInitGuarded = true;
  };

  const hardenInstaStories = () => {
    const InstaStories = customElements.get('insta-stories');
    if (!InstaStories || InstaStories.prototype.__resdorInitGuarded) return;

    ['initStories', 'initButtonsSlider'].forEach((method) => {
      const originalMethod = InstaStories.prototype[method];
      const flag = `__resdor${method}Initialized`;
      InstaStories.prototype[method] = function () {
        if (this[flag]) return;
        this[flag] = true;
        return originalMethod.call(this);
      };
      document.querySelectorAll('insta-stories').forEach((element) => {
        element[flag] = true;
      });
    });
    InstaStories.prototype.__resdorInitGuarded = true;
  };

  const hardenStickyGroupManager = () => {
    const StickyGroupManager = customElements.get('sticky-group-manager');
    if (!StickyGroupManager || StickyGroupManager.prototype.__resdorCleanupGuarded) return;

    const originalConnected = StickyGroupManager.prototype.connectedCallback;
    const originalDisconnected = StickyGroupManager.prototype.disconnectedCallback;
    StickyGroupManager.prototype.connectedCallback = function () {
      const originalAddEventListener = MediaQueryList.prototype.addEventListener;
      const manager = this;
      MediaQueryList.prototype.addEventListener = function (type, listener, options) {
        if (manager.mediaQuery === this && type === 'change') {
          manager.__resdorMediaQueryHandler = listener;
        }
        return originalAddEventListener.call(this, type, listener, options);
      };
      try {
        return originalConnected.call(this);
      } finally {
        MediaQueryList.prototype.addEventListener = originalAddEventListener;
      }
    };
    StickyGroupManager.prototype.disconnectedCallback = function () {
      if (this.mediaQuery && this.__resdorMediaQueryHandler) {
        this.mediaQuery.removeEventListener('change', this.__resdorMediaQueryHandler);
      }
      return originalDisconnected.call(this);
    };
    StickyGroupManager.prototype.__resdorCleanupGuarded = true;
  };

  const hardenPageScrollProgress = () => {
    const PageScrollProgress = customElements.get('page-scroll-progress');
    if (!PageScrollProgress || PageScrollProgress.prototype.__resdorCleanupGuarded) return;

    const originalConnected = PageScrollProgress.prototype.connectedCallback;
    PageScrollProgress.prototype.connectedCallback = function () {
      originalConnected.call(this);
      this.__resdorCleanupReady = true;
    };
    PageScrollProgress.prototype.disconnectedCallback = function () {
      if (this.__resdorCleanupReady && this.update) {
        window.removeEventListener('scroll', this.update);
        window.removeEventListener('resize', this.update);
      }
    };
    PageScrollProgress.prototype.__resdorCleanupGuarded = true;
  };

  hardenContactForm();
  hardenSplide();
  hardenInstaStories();
  hardenStickyGroupManager();
  hardenPageScrollProgress();

  const initialize = () => {
    document.querySelectorAll('comparison-slider').forEach(initializeComparisonSlider);
  };

  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
